/**
 * Utility for line color coding based on type and diameter
 * Used by CustomMap.jsx to display water/sewer lines with diameter-based arrow colors
 */

// Diameter bucket definitions (in inches)
// Uses warm-to-purple progression to avoid confusion with red lead lines
export const DIAMETER_BUCKETS = [
  { max: 2, label: '2" or less', arrowColor: '#A9A9A9' },   // Dark Gray
  { max: 4, label: '4"', arrowColor: '#87CEEB' },           // Sky Blue
  { max: 6, label: '6"', arrowColor: '#FFFFFF' },           // White
  { max: 8, label: '8"', arrowColor: '#FFD700' },           // Yellow (Gold)
  { max: 12, label: '12"', arrowColor: '#FFA500' },         // Orange
  { max: 18, label: '18"', arrowColor: '#DA70D6' },         // Orchid (Light Purple)
  { max: 24, label: '24"', arrowColor: '#9932CC' },         // Dark Orchid (Purple)
  { max: Infinity, label: '36"+', arrowColor: '#4B0082' },  // Indigo (Dark Purple)
];

// Line type color definitions
export const LINE_TYPE_COLORS = {
  water: {
    active: '#0000FF',      // Blue
    inactive: 'lightblue',
  },
  sewer: {
    active: '#008000',      // Green
    inactive: 'lightgreen',
  },
  lead: {
    active: '#FF0000',      // Red
    inactive: 'pink',
  },
  default: {
    active: '#FF0000',      // Red
    inactive: 'pink',
  }
};

/**
 * Get arrow color based on pipe diameter
 * @param {number|string|null} diameter - Pipe diameter in inches
 * @returns {string} Hex color for the arrow
 */
export function getArrowColorByDiameter(diameter) {
  // Handle null, undefined, or non-numeric values
  const numDiameter = parseFloat(diameter);
  
  if (isNaN(numDiameter) || numDiameter <= 0) {
    // Default to first bucket (smallest) if no diameter data
    return DIAMETER_BUCKETS[0].arrowColor;
  }

  // Find the appropriate bucket
  for (const bucket of DIAMETER_BUCKETS) {
    if (numDiameter <= bucket.max) {
      return bucket.arrowColor;
    }
  }

  // Fallback to largest bucket
  return DIAMETER_BUCKETS[DIAMETER_BUCKETS.length - 1].arrowColor;
}

/**
 * Get line color based on asset type and material
 * @param {string} assetType - 'WaterLine' or 'SewerLine'
 * @param {string|null} material - Line material (e.g., 'Lead', 'PVC', etc.)
 * @param {boolean} isActive - Whether the line is selected/active
 * @returns {string} Hex color for the line
 */
export function getLineColor(assetType, material, isActive = true) {
  // Check for lead water lines (special case - red)
  if (assetType === 'WaterLine' && material?.toLowerCase() === 'lead') {
    return isActive ? LINE_TYPE_COLORS.lead.active : LINE_TYPE_COLORS.lead.inactive;
  }

  // Standard water line (blue)
  if (assetType === 'WaterLine') {
    return isActive ? LINE_TYPE_COLORS.water.active : LINE_TYPE_COLORS.water.inactive;
  }

  // Sewer line (green)
  if (assetType === 'SewerLine') {
    return isActive ? LINE_TYPE_COLORS.sewer.active : LINE_TYPE_COLORS.sewer.inactive;
  }

  // Default (red)
  return isActive ? LINE_TYPE_COLORS.default.active : LINE_TYPE_COLORS.default.inactive;
}

/**
 * Get diameter label for display
 * @param {number|string|null} diameter - Pipe diameter in inches
 * @returns {string} Human-readable diameter label
 */
export function getDiameterLabel(diameter) {
  const numDiameter = parseFloat(diameter);
  
  if (isNaN(numDiameter) || numDiameter <= 0) {
    return 'Unknown';
  }

  for (const bucket of DIAMETER_BUCKETS) {
    if (numDiameter <= bucket.max) {
      return bucket.label;
    }
  }

  return DIAMETER_BUCKETS[DIAMETER_BUCKETS.length - 1].label;
}

/**
 * Get all legend data for the line legend component
 * @returns {Object} Legend configuration with line types and diameter buckets
 */
export function getLegendData() {
  return {
    lineTypes: [
      { type: 'Water Line', color: LINE_TYPE_COLORS.water.active },
      { type: 'Sewer Line', color: LINE_TYPE_COLORS.sewer.active },
      { type: 'Lead Water Line', color: LINE_TYPE_COLORS.lead.active },
    ],
    diameterBuckets: DIAMETER_BUCKETS.map(bucket => ({
      label: bucket.label,
      color: bucket.arrowColor,
    })),
  };
}
